import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const NotificationSection = () => {
  const [emailNotifications, setEmailNotifications] = useState({
    incidentAssigned: true,
    incidentUpdated: true,
    incidentEscalated: true,
    slaBreaches: true,
    systemAlerts: false,
    weeklyReports: true,
    securityAlerts: true
  });

  const [slackNotifications, setSlackNotifications] = useState({
    incidentAssigned: true,
    incidentUpdated: false,
    incidentEscalated: true,
    slaBreaches: true,
    systemAlerts: false
  });

  const [smsNotifications, setSmsNotifications] = useState({
    criticalIncidents: true,
    slaBreaches: true,
    securityAlerts: true
  });

  const [severityFilters, setSeverityFilters] = useState({
    email: 'medium',
    slack: 'high',
    sms: 'critical'
  });

  const [escalationTiming, setEscalationTiming] = useState({
    firstEscalation: '15',
    secondEscalation: '30',
    finalEscalation: '60'
  });

  const severityOptions = [
    { value: 'low', label: 'Low and above' },
    { value: 'medium', label: 'Medium and above' },
    { value: 'high', label: 'High and above' },
    { value: 'critical', label: 'Critical only' }
  ];

  const timingOptions = [
    { value: '5', label: '5 minutes' },
    { value: '10', label: '10 minutes' },
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '120', label: '2 hours' }
  ];

  const handleEmailChange = (key, checked) => {
    setEmailNotifications(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSlackChange = (key, checked) => {
    setSlackNotifications(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSmsChange = (key, checked) => {
    setSmsNotifications(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleSeverityChange = (channel, value) => {
    setSeverityFilters(prev => ({
      ...prev,
      [channel]: value
    }));
  };

  const handleTimingChange = (level, value) => {
    setEscalationTiming(prev => ({
      ...prev,
      [level]: value
    }));
  };

  const handleSaveSettings = () => {
    console.log('Saving notification settings:', {
      emailNotifications,
      slackNotifications,
      smsNotifications,
      severityFilters,
      escalationTiming
    });
  };

  const handleTestNotification = (channel) => {
    console.log(`Sending test notification to ${channel}`);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
          <Icon name="Bell" size={24} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Notification Preferences</h3>
          <p className="text-sm text-muted-foreground">Configure how and when you receive notifications</p>
        </div>
      </div>
      <div className="space-y-8">
        {/* Email Notifications */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="Mail" size={20} className="text-primary" />
              <div>
                <h4 className="font-medium text-foreground">Email Notifications</h4>
                <p className="text-sm text-muted-foreground">sarah.johnson@cyberguard.gov</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Send"
              iconPosition="left"
              onClick={() => handleTestNotification('email')}
            >
              Test
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
            <Checkbox
              label="Incident Assigned"
              description="When an incident is assigned to you"
              checked={emailNotifications?.incidentAssigned}
              onChange={(e) => handleEmailChange('incidentAssigned', e?.target?.checked)}
            />
            <Checkbox
              label="Incident Updated"
              description="When incidents you're involved in are updated"
              checked={emailNotifications?.incidentUpdated}
              onChange={(e) => handleEmailChange('incidentUpdated', e?.target?.checked)}
            />
            <Checkbox
              label="Incident Escalated"
              description="When incidents are escalated"
              checked={emailNotifications?.incidentEscalated}
              onChange={(e) => handleEmailChange('incidentEscalated', e?.target?.checked)}
            />
            <Checkbox
              label="SLA Breaches"
              description="When SLA deadlines are approaching or breached"
              checked={emailNotifications?.slaBreaches}
              onChange={(e) => handleEmailChange('slaBreaches', e?.target?.checked)}
            />
            <Checkbox
              label="System Alerts"
              description="System maintenance and updates"
              checked={emailNotifications?.systemAlerts}
              onChange={(e) => handleEmailChange('systemAlerts', e?.target?.checked)}
            />
            <Checkbox
              label="Weekly Reports"
              description="Weekly incident summary reports"
              checked={emailNotifications?.weeklyReports}
              onChange={(e) => handleEmailChange('weeklyReports', e?.target?.checked)}
            />
            <Checkbox
              label="Security Alerts"
              description="Critical security notifications"
              checked={emailNotifications?.securityAlerts}
              onChange={(e) => handleEmailChange('securityAlerts', e?.target?.checked)}
            />
          </div>

          <div className="pl-8">
            <Select
              label="Minimum Severity Level"
              description="Only receive email notifications for incidents at or above this severity"
              options={severityOptions}
              value={severityFilters?.email}
              onChange={(value) => handleSeverityChange('email', value)}
              className="max-w-xs"
            />
          </div>
        </div>

        {/* Slack Notifications */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="MessageSquare" size={20} className="text-secondary" />
              <div>
                <h4 className="font-medium text-foreground">Slack Notifications</h4>
                <p className="text-sm text-muted-foreground">#incident-response channel</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Send"
              iconPosition="left"
              onClick={() => handleTestNotification('slack')}
            >
              Test
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
            <Checkbox
              label="Incident Assigned"
              description="Direct message when assigned"
              checked={slackNotifications?.incidentAssigned}
              onChange={(e) => handleSlackChange('incidentAssigned', e?.target?.checked)}
            />
            <Checkbox
              label="Incident Updated"
              description="Channel updates for incident changes"
              checked={slackNotifications?.incidentUpdated}
              onChange={(e) => handleSlackChange('incidentUpdated', e?.target?.checked)}
            />
            <Checkbox
              label="Incident Escalated"
              description="Channel alerts for escalations"
              checked={slackNotifications?.incidentEscalated}
              onChange={(e) => handleSlackChange('incidentEscalated', e?.target?.checked)}
            />
            <Checkbox
              label="SLA Breaches"
              description="Urgent channel notifications"
              checked={slackNotifications?.slaBreaches}
              onChange={(e) => handleSlackChange('slaBreaches', e?.target?.checked)}
            />
            <Checkbox
              label="System Alerts"
              description="System status updates"
              checked={slackNotifications?.systemAlerts}
              onChange={(e) => handleSlackChange('systemAlerts', e?.target?.checked)}
            />
          </div>

          <div className="pl-8">
            <Select
              label="Minimum Severity Level"
              description="Only receive Slack notifications for incidents at or above this severity"
              options={severityOptions}
              value={severityFilters?.slack}
              onChange={(value) => handleSeverityChange('slack', value)}
              className="max-w-xs"
            />
          </div>
        </div>

        {/* SMS Notifications */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="Smartphone" size={20} className="text-warning" />
              <div>
                <h4 className="font-medium text-foreground">SMS Notifications</h4>
                <p className="text-sm text-muted-foreground">+1 (555) 123-4567</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Send"
              iconPosition="left"
              onClick={() => handleTestNotification('sms')}
            >
              Test
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-8">
            <Checkbox
              label="Critical Incidents"
              description="Immediate SMS for critical incidents"
              checked={smsNotifications?.criticalIncidents}
              onChange={(e) => handleSmsChange('criticalIncidents', e?.target?.checked)}
            />
            <Checkbox
              label="SLA Breaches"
              description="SMS alerts for SLA violations"
              checked={smsNotifications?.slaBreaches}
              onChange={(e) => handleSmsChange('slaBreaches', e?.target?.checked)}
            />
            <Checkbox
              label="Security Alerts"
              description="Emergency security notifications"
              checked={smsNotifications?.securityAlerts}
              onChange={(e) => handleSmsChange('securityAlerts', e?.target?.checked)}
            />
          </div>

          <div className="pl-8">
            <Select
              label="Minimum Severity Level"
              description="Only receive SMS notifications for incidents at or above this severity"
              options={severityOptions}
              value={severityFilters?.sms}
              onChange={(value) => handleSeverityChange('sms', value)}
              className="max-w-xs"
            />
          </div>
        </div>

        {/* Escalation Timing */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Icon name="Clock" size={20} className="text-error" />
            <div>
              <h4 className="font-medium text-foreground">Escalation Timing</h4>
              <p className="text-sm text-muted-foreground">Configure when to receive escalation notifications</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-8">
            <Select
              label="First Escalation"
              description="Initial escalation reminder"
              options={timingOptions}
              value={escalationTiming?.firstEscalation}
              onChange={(value) => handleTimingChange('firstEscalation', value)}
            />
            <Select
              label="Second Escalation"
              description="Follow-up escalation"
              options={timingOptions}
              value={escalationTiming?.secondEscalation}
              onChange={(value) => handleTimingChange('secondEscalation', value)}
            />
            <Select
              label="Final Escalation"
              description="Final escalation warning"
              options={timingOptions}
              value={escalationTiming?.finalEscalation}
              onChange={(value) => handleTimingChange('finalEscalation', value)}
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-border">
          <Button
            variant="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSaveSettings}
          >
            Save Notification Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSection;